import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import AppPageTitle from "../../components/AppPageTitle";
import { i18n } from "../../translate/i18n";
import IntegrationCard from "./components/IntegrationCard";
import IntegrationConfigModal from "./components/IntegrationConfigModal";
import { INTEGRATIONS } from "./integrationsCatalog";
import apiV2 from "../../services/apiV2";
import { toast } from "react-toastify";

export default function Integrations() {
	const [selectedIntegration, setSelectedIntegration] = useState(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [integrations, setIntegrations] = useState([]);
	const location = useLocation();
	const history = useHistory();

	const loadIntegrations = useCallback(async () => {
		try {
			const { data } = await apiV2.get("/integrations");

			const merged = INTEGRATIONS.map((integration) => {
				const config = data.find((i) => i.name === integration.id);

				return {
					...integration,
					config,
					status: config?.token ? "connected" : integration.status,
				};
			});

			setIntegrations(merged);
		} catch (error) {
			toast.error(
				error.response?.data?.error || i18n.t("integrations.loadError")
			);
		}
	}, []);

	useEffect(() => {
		loadIntegrations();
	}, [loadIntegrations]);

	useEffect(() => {
		const onMessage = (event) => {
			const payload = event.data;
			if (!payload || payload.type !== "google-calendar-oauth") return;

			if (payload.status === "connected") {
				toast.success(i18n.t("integrations.googleCalendar.connectSuccess"));
				loadIntegrations();
				return;
			}
			if (payload.status === "error") {
				toast.error(i18n.t("integrations.googleCalendar.connectError"));
			}
		};

		window.addEventListener("message", onMessage);
		return () => window.removeEventListener("message", onMessage);
	}, [loadIntegrations]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const googleStatus = params.get("googleCalendar");
		if (!googleStatus) return;

		if (googleStatus === "connected") {
			toast.success(i18n.t("integrations.googleCalendar.connectSuccess"));
			loadIntegrations();
		} else if (googleStatus === "error") {
			toast.error(i18n.t("integrations.googleCalendar.connectError"));
		}

		history.replace("/integrations");
	}, [location.search, history, loadIntegrations]);

	const handleConfigure = (integration) => {
		setSelectedIntegration(integration);
		setModalOpen(true);
	};

	const handleCloseModal = () => {
		setModalOpen(false);
		setSelectedIntegration(null);
	};

	return (
		<div className="app-admin-page">
			<AppPageTitle
				title={i18n.t("integrations.title")}
				subtitle={i18n.t("integrations.subtitle")}
			/>

			<div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
				{integrations.map((integration) => (
					<IntegrationCard
						key={integration.id}
						integration={integration}
						onConfigure={handleConfigure}
					/>
				))}
			</div>

			<IntegrationConfigModal
				open={modalOpen}
				integration={selectedIntegration}
				onClose={handleCloseModal}
				onSaved={loadIntegrations}
			/>
		</div>
	);
}
